import { customElement, property, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import './d-field-section';
import { nothing } from 'lit';
import { html, LitElement } from 'lit';
import type { FormUserValue, SectionField, SectionFieldData } from './section-field';
import type { FormElement, FormElementEditor, FormElementOption, FormElementRadio } from 'src/library/components/form';
import type { FormMode } from 'src/library/components/form';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import {
  getAllValues,
  getUserValue,
  isEditor,
  isRadio,
  getFormContentWithValuesForView,
  hasColoredOptions,
} from 'src/store/selectors/form-functions';
import type { BarGraphItem } from 'src/library/elements/bar-graph-item';
import 'src/library/elements/d-bar-graph';

/**
 *
 */
@customElement('d-form')
export class DForm extends LitElement {
  @property({ type: String })
  mode: FormMode = 'viewAsRespondent';
  @property({ type: Array })
  content: FormElement[] = [];
  @property({ type: Boolean })
  isSurvey = false;
  @property({ type: Boolean })
  anonymous = false;
  @property({ type: Array })
  userNames: SelectDropdownOption[] = [];
  @property({ type: Array })
  respondents: SelectDropdownOption[] = [];
  @property({ type: String })
  currentUserUuid = '';
  @property({ type: Number })
  contentStickyTop = 0;

  @state()
  editElements: string[] = [];

  private get editFields(): SectionField[] {
    const result: SectionField[] = [];
    this.content.forEach((element: FormElement) => {
      let underLabel: string = ``;
      if (element.description) {
        underLabel = `<p class="section-description">${element.description}</p>`;
      }
      result.push({
        field: element.path,
        type: 'header',
        size: 'verybig',
        sticky: true,
        label: 'label' in element ? element.label : '',
        underLabel,
      });
      if (element.type === 'section' || element.type === 'conditionalSection') {
        this.editFieldsFromElements(element.elements ?? []).forEach((element) => {
          result.push(element);
        });
      }
    });
    return result;
  }

  calculateEditItem() {
    const editItem: Record<string, string | number | undefined> = {};
    const formElementsForEditItem = this.getFormElementsForEditItem(this.content);
    formElementsForEditItem.forEach((elm) => {
      editItem[elm.field] = elm.value;
    });
    return editItem;
  }

  getContentWithUpdatedValues(elements: FormElement[], editItem: Record<string, any>): FormElement[] {
    return elements.map((element) => {
      // Copy the top-level props of this element
      const base: FormElement = { ...element };

      // If it's an editor we care about, update or add the user’s value
      if (
        isEditor(element) &&
        this.fieldTypeForEditMode(element)?.includes('EDIT') &&
        element.path &&
        'values' in base
      ) {
        // Work with a fresh copy of the values array
        let newValues = element.values.slice();

        // Find existing entry for this user
        const idx = newValues.findIndex((v) => v.userUuid === this.currentUserUuid);
        const newValue = editItem[element.path];

        if (idx >= 0) {
          // If empty, remove; else replace with updated object
          if (newValue === '' || newValue == null) {
            newValues = newValues.filter((v) => v.userUuid !== this.currentUserUuid);
          } else {
            newValues = newValues.map((v, i) => (i === idx ? { ...v, value: newValue } : v));
          }
        } else {
          // No existing entry: only add if non-empty
          if (newValue !== '' && newValue != null) {
            newValues = [...newValues, { userUuid: this.currentUserUuid, value: newValue }];
          }
        }

        base.values = newValues;
      }

      // Recurse into nested sections without mutating
      if (
        (element.type === 'section' || element.type === 'conditionalSection') &&
        'elements' in base &&
        base.elements
      ) {
        base.elements = this.getContentWithUpdatedValues(base.elements, editItem);
      }
      return base;
    });
  }

  editElement(e) {
    const editElements = [...this.editElements];
    editElements.push(e.detail.value);
    this.editElements = editElements;
  }

  coloredBullet(color: string) {
    return (
      '<span style="display:inline-block;width:10px;height:10px;margin-left:-16px;margin-right:8px;border-radius:50%;background:' +
      color +
      '"></span>'
    );
  }

  getValuesAsHtmlList(values: FormUserValue[]): string {
    if (values.length) {
      let list = '<ul class="none">';
      values.forEach((value) => {
        if (value.value) {
          list += '<li>';
          if (value.optionColor) {
            list += this.coloredBullet(value.optionColor);
          }
          list += value.value + '&nbsp;&nbsp;&nbsp;<span class="sublabel">' + value.userName + '</span></li>';
        }
      });
      list += '</ul>';
      return list;
    }
    return '';
  }

  viewFieldValueAsHtml(element: FormElement): string {
    if (isEditor(element) && element.values && element.values.length) {
      if (!this.isSurvey || this.mode === 'viewAsResponsible' || this.mode === 'editAsResponsible') {
        let options: FormElementOption[] | undefined = undefined;
        if (isRadio(element)) {
          options = element.options;
        }
        const values = getAllValues(this.userNames, element.type, options, element.values);
        return this.getValuesAsHtmlList(values);
      }
      return getUserValue(this.currentUserUuid, element, true);
    }
    return '';
  }

  private renderViewHtmlWithGraphForEdit(element: FormElementRadio) {
    const items: BarGraphItem[] = element.options.map((o) => {
      const count = element.values.filter((v) => {
        return v.value === o.value;
      }).length;
      return { count, color: o.color, label: o.value };
    });
    const unanswered = this.respondents.length - element.values.length;
    items.push({ count: unanswered, color: 'var(--backgroundGray)', label: t('forms.notAnswered') });
    return html` <d-view-html
      wrap-label
      .field=${element.path ?? ''}
      .label=${element.label ?? ''}
      .value=${this.anonymous ? '' : this.viewFieldValueAsHtml(element)}
      .expandable=${!this.anonymous && element.values.length > 0}
      class="fullWidth"
      ><div slot="under-label">
        ${element.description ? html`<p class="description small">${element.description}</p>` : nothing}
        <d-bar-graph .items=${items}></d-bar-graph>
      </div>
    </d-view-html>`;
  }

  private renderViewHtmlWithGraphForView(
    label: string,
    value: string,
    description: string,
    barGraphItems: BarGraphItem[],
    expandable: boolean,
  ) {
    return html` <d-view-html wrap-label .label=${label} .value=${value} class="fullWidth" .expandable=${expandable}
      ><div slot="under-label">
        ${description ? html`<p class="description small">${description}</p>` : nothing}
        <d-bar-graph .items=${barGraphItems}></d-bar-graph>
      </div>
    </d-view-html>`;
  }

  viewFieldsFromFieldsData(data: SectionFieldData[]): SectionField[] {
    const result: SectionField[] = [];
    data.forEach((d) => {
      let value = '';
      if (d.value) {
        value = d.value;
      }
      if (d.userValues) {
        value = this.getValuesAsHtmlList(d.userValues);
      }
      if (d.type === 'header') {
        let underLabel = '';
        if (d.description) {
          underLabel = `<p class="section-description">${d.description}</p>`;
        }
        result.push({
          field: d.field,
          label: d.label,
          type: 'header',
          size: 'verybig',
          underLabel,
        });
      }
      if (d.type === 'view-html-with-graph') {
        let expandable = false;
        if (!this.anonymous && d.userValues && d.userValues.length > 0) {
          expandable = true;
        }
        result.push({
          field: d.field,
          type: 'custom',
          render: () =>
            this.renderViewHtmlWithGraphForView(d.label, value, d.description ?? '', d.barGraphItems ?? [], expandable),
        });
      }
      if (d.type === 'view-html') {
        result.push({
          field: d.field,
          label: d.label,
          wrapLabel: true,
          type: 'view-html',
          value: () => value,
          class: 'reduce-label-distance',
        });
      }
    });
    return result;
  }

  render() {
    let fields: SectionField[];
    if (this.mode === 'viewAsResponsible' || this.mode === 'viewAsRespondent') {
      const formContentWithValuesForView = getFormContentWithValuesForView(
        this.content,
        this.mode,
        this.userNames,
        this.currentUserUuid,
        this.respondents,
        this.anonymous,
      );
      fields = this.viewFieldsFromFieldsData(formContentWithValuesForView);
    } else {
      fields = this.editFields;
    }
    const e = this.calculateEditItem();

    return html`
      <d-field-section
        .entityType=${'forms'}
        .fields=${fields}
        .item=${e}
        .contentStickyTop=${this.contentStickyTop}
        @item-changed=${(e) => this.onFieldSectionChanged(e)}
        @field-action=${(e) => this.editElement(e)}
      ></d-field-section>
    `;
  }

  protected onFieldSectionChanged(e) {
    e.stopPropagation();
    this.content = this.getContentWithUpdatedValues(this.content, e.detail.value);
    this.dispatchEvent(
      new CustomEvent('item-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.content },
      }),
    );
  }

  private getAllValuesExceptForCurrentUser(element: FormElementEditor): string {
    const elm = { ...element };
    elm.values = elm.values.filter((value) => {
      return value.userUuid !== this.currentUserUuid && value.value;
    });
    let options: FormElementOption[] | undefined = undefined;
    if (isRadio(elm)) {
      options = elm.options;
    }
    const values = getAllValues(this.userNames, elm.type, options, elm.values);
    return this.getValuesAsHtmlList(values);
  }

  private someValueMatchesSomeCondition(values: string[], condition: string[]) {
    return values.some((item) => condition.includes(item));
  }

  private answeredByCurrentUser(element: FormElementEditor) {
    return (
      element.values.find((v) => {
        return v.userUuid === this.currentUserUuid && v.value;
      }) !== undefined
    );
  }

  private answeredByOtherThanCurrentUser(element: FormElementEditor) {
    return (
      element.values.find((v) => {
        return v.userUuid !== this.currentUserUuid && v.value;
      }) !== undefined
    );
  }

  private answeredButNotByCurrentUser(element: FormElementEditor) {
    return this.answeredByOtherThanCurrentUser(element) && !this.answeredByCurrentUser(element);
  }

  private answeredByCurrentUserAndOthers(element: FormElementEditor) {
    return this.answeredByCurrentUser(element) && this.answeredByOtherThanCurrentUser(element);
  }

  private getSectionFieldForEdit(
    element: FormElement,
    type: string,
    value: string,
    otherValues: string = '',
  ): SectionField {
    let underLabel = '';
    if (element.description) {
      underLabel = `<p class="description">${element.description}</p>`;
    } else if (otherValues) {
      underLabel = otherValues + '<p class="sublabel">' + t('forms.myAnswerLabel') + '</p>';
    }
    if (type === 'input') {
      if (element.type === 'text') {
        return {
          field: element.path ?? '',
          type: 'edit-text',
          label: element.label,
          wrapLabel: true,
          class: 'fullWidth',
          underLabel,
        };
      }
      if (element.type === 'textarea') {
        return {
          field: element.path ?? '',
          type: 'edit-textarea',
          label: element.label,
          wrapLabel: true,
          class: 'fullWidth',
          underLabel,
        };
      }
      if (element.type === 'date') {
        return {
          field: element.path ?? '',
          type: 'edit-date',
          label: element.label,
          wrapLabel: true,
          class: 'fullWidth',
          clear: true,
          today: true,
          underLabel,
        };
      } else if (element.type === 'radio') {
        return {
          field: element.path ?? '',
          type: 'edit-radio',
          label: element.label,
          wrapLabel: true,
          options: element.options.map((o) => {
            return { value: o.value, text: o.value, color: o.color ?? '' };
          }),
          class: 'fullWidth',
          underLabel,
        };
      } else if (element.type === 'check') {
        return {
          field: element.path ?? '',
          type: 'edit-checkbox',
          label: element.label,
          wrapLabel: true,
          class: 'fullWidth',
          underLabel,
        };
      }
    }
    if (
      element.type === 'radio' &&
      hasColoredOptions(element.options) &&
      element.responseType === 'multiple' &&
      this.respondents.length > 0 &&
      (this.mode === 'viewAsResponsible' || this.mode === 'editAsResponsible')
    ) {
      return {
        field: element.path ?? '',
        type: 'custom',
        render: () => this.renderViewHtmlWithGraphForEdit(element),
      };
    } else {
      const viewField: SectionField = {
        field: element.path ?? '',
        label: element.label,
        wrapLabel: true,
        value: () => value,
        type: 'view-html',
        class: 'reduce-label-distance',
      };
      if (!this.isSurvey && this.mode === 'editAsResponsible' && !this.editElements.includes(element.path)) {
        viewField.action = { name: t('forms.addOwnAnswer'), action: element.path };
      }
      return viewField;
    }
  }

  private fieldTypeForEditMode(element: FormElementEditor): 'EDIT' | 'VIEW' | 'VIEW EDIT' | undefined {
    if (!this.isSurvey) {
      // hvis brukeren har trykket på rediger i et view field
      if ('path' in element && element.path && this.editElements.includes(element.path)) {
        return 'VIEW EDIT';
      }
      // hvis både brukeren og andre har svart
      if (this.answeredByCurrentUserAndOthers(element)) {
        return 'VIEW EDIT';
      }
    }
    if (this.mode === 'editAsResponsible') {
      if (element.editors === 'respondents') {
        return 'VIEW';
      } else {
        if (element.responseType === 'single') {
          if (this.answeredButNotByCurrentUser(element)) {
            return 'VIEW';
          } else {
            return 'EDIT';
          }
        }
        if (element.responseType === 'multiple') {
          return 'EDIT';
        }
      }
    }
    if (this.mode === 'editAsRespondent' && element.editors !== 'responsibles') {
      if (element.responseType === 'single') {
        if (this.answeredButNotByCurrentUser(element)) {
          return 'VIEW';
        } else {
          return 'EDIT';
        }
      }
      if (element.responseType === 'multiple') {
        return 'EDIT';
      }
    }
  }

  private editFieldsFromElements(elements: FormElement[]): SectionField[] {
    const result: SectionField[] = [];
    let fieldValue = '';
    let fieldValues: string[] = [];
    elements.forEach((element) => {
      if (isEditor(element)) {
        fieldValue = getUserValue(this.currentUserUuid, element);
        fieldValues = element.values.map((v) => {
          return v.value;
        });
        if (this.fieldTypeForEditMode(element) === 'VIEW') {
          let options: FormElementOption[] | undefined = undefined;
          if (isRadio(element)) {
            options = element.options;
          }
          const values: FormUserValue[] = getAllValues(this.userNames, element.type, options, element.values);
          result.push(this.getSectionFieldForEdit(element, 'view', this.getValuesAsHtmlList(values)));
        }
        if (this.fieldTypeForEditMode(element) === 'VIEW EDIT') {
          result.push(
            this.getSectionFieldForEdit(
              element,
              'input',
              getUserValue(this.currentUserUuid, element),
              this.getAllValuesExceptForCurrentUser(element),
            ),
          );
        }
        if (this.fieldTypeForEditMode(element) === 'EDIT') {
          result.push(this.getSectionFieldForEdit(element, 'input', getUserValue(this.currentUserUuid, element)));
        }
      } else if (element.type === 'conditionalSection') {
        let hasViewElement = false;
        element.elements.forEach((e) => {
          if (isEditor(e) && this.fieldTypeForEditMode(e) === 'VIEW') {
            hasViewElement = true;
          }
        });
        let opened = this.someValueMatchesSomeCondition(fieldValues, element.condition);
        if (this.isSurvey) {
          opened = element.condition.includes(fieldValue) || hasViewElement;
        }
        result.push({
          type: 'expansion',
          opened,
          resetHiddenFields: true,
          fields: this.editFieldsFromElements(element.elements),
        });
      }
    });
    return result;
  }

  private getFormElementsForEditItem(elements: FormElement[]): { field: string; value: string }[] {
    const result: { field: string; value: string }[] = [];
    elements.forEach((element) => {
      if (element.path && isEditor(element)) {
        if (this.fieldTypeForEditMode(element)?.includes('EDIT')) {
          result.push({ field: element.path, value: getUserValue(this.currentUserUuid, element) });
        }
        if (this.fieldTypeForEditMode(element) === 'VIEW') {
          result.push({
            field: element.path,
            value: this.viewFieldValueAsHtml(element),
          });
        }
      }
      if (element.type === 'section' || element.type === 'conditionalSection') {
        this.getFormElementsForEditItem(element.elements ?? []).forEach((e) => {
          result.push(e);
        });
      }
    });
    return result;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-form': DForm;
  }
}
